import React, { useState, useEffect } from 'react';
import { Search, X, RefreshCw } from 'react-feather';
import axios from 'axios';
import { baseURL, imageURL } from '../../Services/api/baseURL';
import dummyImg from '../../assets/images/product/2.png';

const getToken = () => {
    const rawToken = localStorage.getItem("token");
    let token = rawToken;
    try { token = JSON.parse(rawToken); } catch (e) { }
    return token ? `Bearer ${token}` : "";
};

const ProductSearch = ({ onProductSelect, placeholder = "Search products...", selectedIds = [], storeId, singleSelect = false, allowGlobalSearch = false }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [localSelectedVariants, setLocalSelectedVariants] = useState([]);

    const [categoryFilter, setCategoryFilter] = useState("");
    const [subCategoryFilter, setSubCategoryFilter] = useState("");
    const [categoryMap, setCategoryMap] = useState({});
    const [subCategoryMap, setSubCategoryMap] = useState({});

    useEffect(() => {
        if (!isOpen) {
            setSearchTerm('');
            setSearchResults([]);
            setLocalSelectedVariants([]);
            setCategoryFilter('');
            setSubCategoryFilter('');
            return;
        }

        const token = getToken();
        axios.get(`${baseURL}/api/admin/get-collections?page=1&limit=1000`, { headers: { Authorization: token } })
            .then(r => {
                console.log(r, "category")
                const map = {};
                (r.data.data || []).forEach(c => { map[c._id] = c.collection_name; });
                setCategoryMap(map);
            }).catch(() => { });
        axios.get(`${baseURL}/api/admin/get-sub-collections?page=1&limit=1000`, { headers: { Authorization: token } })
            .then(r => {
                const map = {};
                (r.data.data || []).forEach(s => { map[s._id] = s.sub_collection_name; });
                setSubCategoryMap(map);
            }).catch(() => { });
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return;

        const delayDebounceFn = setTimeout(async () => {
            setIsSearching(true);
            try {
                const token = getToken();
                const response = await axios.get(`${baseURL}/api/admin/inventory/products/search`, {
                    params: {
                        search: searchTerm,
                        storeId: storeId || undefined
                    },
                    headers: { Authorization: token }
                });
                console.log(response)

                if (response.data && response.data.data) {
                    setSearchResults(response.data.data);
                } else if (Array.isArray(response.data)) {
                    setSearchResults(response.data);
                }
            } catch (err) {
                console.error("Search failed", err);
            } finally {
                setIsSearching(false);
            }
        }, 300);

        return () => clearTimeout(delayDebounceFn);
    }, [searchTerm, isOpen, storeId]);

    const handleToggleSelect = (item) => {
        if (singleSelect) {
            setLocalSelectedVariants(prev => {
                const exists = prev.some(v => v.productVariantId === item.productVariantId);
                if (exists) {
                    return [];
                } else {
                    return [item];
                }
            });
            return;
        }
        setLocalSelectedVariants(prev => {
            const exists = prev.some(v => v.productVariantId === item.productVariantId);
            if (exists) {
                return prev.filter(v => v.productVariantId !== item.productVariantId);
            } else {
                return [...prev, item];
            }
        });
    };

    const handleAddSelected = () => {
        localSelectedVariants.forEach(variant => {
            onProductSelect(variant);
        });
        setIsOpen(false);
    };

    // Generate options based on all products in searchResults
    const categoryOptions = [...new Map(
        searchResults.filter(p => p.categoryId).map(p => {
            const id = p.categoryId.toString();
            const name = categoryMap[id] || id;
            return [id, { id, name }];
        })
    ).values()];

    const subCategoryOptions = [...new Map(
        searchResults.filter(p => {
            if (!categoryFilter) return !!p.subCategoryId;
            const cid = p.categoryId?.toString();
            return cid === categoryFilter && !!p.subCategoryId;
        }).map(p => {
            const id = p.subCategoryId.toString();
            const name = subCategoryMap[id] || id;
            return [id, { id, name }];
        })
    ).values()];

    // Filter list locally
    const filteredProducts = searchResults.filter(p => {
        if (categoryFilter) {
            const cid = (p.categoryId || "").toString();
            if (cid !== categoryFilter) return false;
        }
        if (subCategoryFilter) {
            const sid = (p.subCategoryId || "").toString();
            if (sid !== subCategoryFilter) return false;
        }
        return true;
    });

    const isDisabled = storeId !== undefined && !storeId && !allowGlobalSearch;
    return (
        <div className="position-relative mb-3">
            <button
                type="button"
                className="w-full flex items-center gap-2 text-sm text-gray-400 bg-gray-50 border border-dashed border-gray-200 rounded-xl px-4 py-3 hover:border-[#d3178a] hover:text-[#d3178a] transition-all text-start disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() => setIsOpen(true)}
                disabled={isDisabled}
                style={{ cursor: isDisabled ? 'not-allowed' : 'pointer', outline: 'none' }}
            >
                <Search size={14} />
                <span>{isDisabled ? "Please select Source Store first" : placeholder}</span>
            </button>

            {isOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.5)", position: 'fixed', zIndex: 9999 }}>
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[80vh] flex flex-col overflow-hidden animate-fade-in">
                        {/* Header */}
                        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                            <h3 className="text-base font-semibold text-gray-900 m-0">Add products</h3>
                            <button onClick={() => setIsOpen(false)} className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-400 border-0 bg-transparent" style={{ cursor: 'pointer' }}>
                                <X size={15} />
                            </button>
                        </div>

                        {/* Search & Filters */}
                        <div className="px-5 py-3 border-b border-gray-100 space-y-2">
                            <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 focus-within:border-[#d3178a]">
                                <Search size={14} className="text-gray-400 flex-shrink-0" />
                                <input
                                    value={searchTerm}
                                    onChange={e => setSearchTerm(e.target.value)}
                                    placeholder="Search products..."
                                    autoFocus
                                    className="bg-transparent text-sm text-gray-700 outline-none flex-1 border-0 w-full"
                                />
                            </div>
                            <div className="flex gap-2">
                                <select
                                    value={categoryFilter}
                                    onChange={e => { setCategoryFilter(e.target.value); setSubCategoryFilter(""); }}
                                    className="flex-1 h-9 border border-gray-200 rounded-lg px-3 text-sm text-gray-700 bg-gray-50 focus:outline-none focus:border-[#d3178a]"
                                    style={{ cursor: 'pointer' }}
                                >
                                    <option value="">All Categories</option>
                                    {categoryOptions.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                </select>
                                <select
                                    value={subCategoryFilter}
                                    onChange={e => setSubCategoryFilter(e.target.value)}
                                    disabled={!categoryFilter}
                                    className="flex-1 h-9 border border-gray-200 rounded-lg px-3 text-sm text-gray-700 bg-gray-50 focus:outline-none focus:border-[#d3178a] disabled:opacity-40 disabled:cursor-not-allowed"
                                    style={{ cursor: 'pointer' }}
                                >
                                    <option value="">All Sub-Categories</option>
                                    {subCategoryOptions.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                                </select>
                            </div>
                        </div>

                        {/* Results Table */}
                        <div className="overflow-y-auto flex-1" style={{ minHeight: '200px' }}>
                            {isSearching && searchResults.length === 0 ? (
                                <div className="flex justify-center py-10">
                                    <RefreshCw size={20} className="animate-spin text-gray-300" />
                                </div>
                            ) : filteredProducts.length === 0 ? (
                                <p className="text-sm text-gray-400 text-center py-10">
                                    No products found
                                </p>
                            ) : (
                                <table className="w-full text-sm table-fixed border-collapse m-0">
                                    <colgroup>
                                        <col style={{ width: '45px' }} />
                                        <col />
                                        <col style={{ width: '170px' }} />
                                        {storeId && <col style={{ width: '90px' }} />}
                                    </colgroup>
                                    <thead>
                                        <tr className="bg-gray-50 border-b border-gray-100">
                                            <th className="ps-4 pe-0 py-2.5 text-left text-[10px] font-semibold text-gray-400 uppercase border-0"></th>
                                            <th className="px-4 py-2.5 text-left text-[10px] font-semibold text-gray-400 uppercase border-0">Product</th>
                                            <th className="px-4 py-2.5 text-left text-[10px] font-semibold text-gray-400 uppercase border-0">SKU</th>
                                            {storeId && <th className="px-4 py-2.5 text-left text-[10px] font-semibold text-gray-400 uppercase border-0">Available</th>}
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-50">
                                        {filteredProducts.map(item => {
                                            const isAlreadyAdded = selectedIds.includes(item.productVariantId);
                                            const isSelectedThisSession = localSelectedVariants.some(v => v.productVariantId === item.productVariantId);
                                            const imageSrc = item.variantImage
                                                ? (item.variantImage.startsWith('http') ? item.variantImage : `${imageURL}${item.variantImage}`)
                                                : dummyImg;

                                            return (
                                                <tr
                                                    key={item.productVariantId}
                                                    className={isAlreadyAdded || isSelectedThisSession ? "bg-gray-50" : ""}
                                                    onClick={() => { if (!isAlreadyAdded) handleToggleSelect(item); }}
                                                    style={{ cursor: isAlreadyAdded ? 'not-allowed' : 'pointer' }}
                                                >
                                                    <td className="ps-4 pe-0 py-3 text-left border-0" onClick={e => e.stopPropagation()}>
                                                        <div className="flex items-center justify-center">
                                                            <input
                                                                type="checkbox"
                                                                id={`checkbox-${item.productVariantId}`}
                                                                style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }}
                                                                checked={isAlreadyAdded || isSelectedThisSession}
                                                                disabled={isAlreadyAdded}
                                                                onChange={() => handleToggleSelect(item)}
                                                            />
                                                            <label
                                                                htmlFor={`checkbox-${item.productVariantId}`}
                                                                className="d-flex align-items-center justify-content-center border rounded-circle"
                                                                style={{
                                                                    width: '20px',
                                                                    height: '20px',
                                                                    margin: 0,
                                                                    cursor: isAlreadyAdded ? 'not-allowed' : 'pointer',
                                                                    backgroundColor: isAlreadyAdded ? '#e9ecef' : (isSelectedThisSession ? '#d3178a' : '#ffffff'),
                                                                    borderColor: isAlreadyAdded ? '#ced4da' : (isSelectedThisSession ? '#d3178a' : '#ced4da'),
                                                                    color: '#ffffff',
                                                                    transition: 'all 0.2s ease-in-out'
                                                                }}
                                                            >
                                                                {(isAlreadyAdded || isSelectedThisSession) && (
                                                                    <svg
                                                                        viewBox="0 0 24 24"
                                                                        fill="none"
                                                                        stroke={isAlreadyAdded ? '#6c757d' : '#ffffff'}
                                                                        strokeWidth="3.5"
                                                                        strokeLinecap="round"
                                                                        strokeLinejoin="round"
                                                                        style={{ width: '12px', height: '12px' }}
                                                                    >
                                                                        <polyline points="20 6 9 17 4 12" />
                                                                    </svg>
                                                                )}
                                                            </label>
                                                        </div>
                                                    </td>
                                                    <td className="px-4 py-3 text-left border-0">
                                                        <div className="flex items-center gap-3">
                                                            <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0 overflow-hidden">
                                                                <img
                                                                    src={imageSrc}
                                                                    alt={item.productName}
                                                                    className="w-full h-full object-cover"
                                                                    onError={e => { e.target.src = dummyImg; }}
                                                                />
                                                            </div>
                                                            <div className="min-w-0 flex-1">
                                                                <p className={`font-medium text-sm truncate m-0 ${isAlreadyAdded ? "text-gray-400" : "text-gray-800"}`} title={item.productName}>
                                                                    {item.productName}
                                                                </p>
                                                                <p className="text-xs text-gray-400 truncate m-0 mt-0.5">{item.variantName}</p>
                                                                {isAlreadyAdded && (
                                                                    <p className="text-xs text-amber-600 font-medium truncate m-0 mt-0.5">Item already selected</p>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-4 py-3 text-left border-0">
                                                        <span className={`text-sm ${isAlreadyAdded || isSelectedThisSession ? "text-gray-400" : "text-gray-600"}`}>{item.sku}</span>
                                                    </td>
                                                    {storeId && (
                                                        <td className="px-6 py-3 text-left border-0">
                                                            <span className={`text-sm font-semibold ${isAlreadyAdded || isSelectedThisSession ? "text-gray-400" : "text-success"}`}>
                                                                {item.quantity || 0}
                                                            </span>
                                                        </td>
                                                    )}
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            )}
                        </div>

                        {/* Footer */}
                        <div className="px-5 py-3.5 border-t border-gray-100 flex items-center justify-between bg-white">
                            <div className="text-xs bg-gray-100 border border-gray-200 px-3 py-1.5 rounded-lg text-gray-500 font-semibold">
                                {localSelectedVariants.length} variants selected
                            </div>
                            <div className="flex gap-2">
                                <button
                                    type="button"
                                    onClick={() => setIsOpen(false)}
                                    className="text-sm font-semibold px-4 py-2 rounded-xl border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 transition-all border-solid"
                                    style={{ cursor: 'pointer' }}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={handleAddSelected}
                                    disabled={localSelectedVariants.length === 0}
                                    className="text-sm font-semibold px-5 py-2 rounded-xl text-white border-0 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                                    style={{ background: "#d3178a", cursor: localSelectedVariants.length === 0 ? 'not-allowed' : 'pointer' }}
                                >
                                    Add
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProductSearch;
